import { useCallback } from 'react';
import { redirect } from 'react-router-dom';
import { useGetUserPermissions } from '.';
import { ACTIVE_TAB_PARAM } from '../constants';
import { Roles, SimulationsTab } from '../types';
import useAppSelector from './useAppSelector';

const NON_SALES_REP_DEFAULT_TAB = SimulationsTab.LIBRARY;
const SALES_REPS_DEFAULT_TAB = SimulationsTab.FYP;

/**
 * Returns a Router loader for the Simulations page.
 */
const useSimulationsLoader = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isSalesRep = user?.role === Roles.SALES_REP;

  const { canUserViewLibrary, canUserCreateSims, isLoading: isOrgConfigsLoading } = useGetUserPermissions();

  const simulationsLoader = useCallback(
    ({ request }: { request: Request }) => {
      if (isOrgConfigsLoading) {
        return null;
      }

      const url = new URL(request.url);
      const activeTab = url.searchParams.get(ACTIVE_TAB_PARAM);

      const validTabs = Object.values(SimulationsTab).filter(
        (tab) =>
          (tab !== SimulationsTab.LIBRARY && tab !== SimulationsTab.FYP && tab !== SimulationsTab.DRAFTS) ||
          (tab === SimulationsTab.LIBRARY && canUserViewLibrary) ||
          (tab === SimulationsTab.FYP && isSalesRep) ||
          (tab === SimulationsTab.DRAFTS && canUserCreateSims)
      );

      const isValidTab = validTabs.includes(activeTab as SimulationsTab);

      const hasTab = !!activeTab && isValidTab;

      // If no tab is selected, set the default tab.
      if (!hasTab) {
        const defaultTab = isSalesRep ? SALES_REPS_DEFAULT_TAB : NON_SALES_REP_DEFAULT_TAB;
        if (validTabs.includes(defaultTab)) {
          url.searchParams.set(ACTIVE_TAB_PARAM, defaultTab);
          return redirect(url.pathname + url.search);
        }
      }

      // If no redirects were needed, return null to continue with the current URL
      return null;
    },
    [canUserCreateSims, canUserViewLibrary, isOrgConfigsLoading, isSalesRep]
  );

  return simulationsLoader;
};

export default useSimulationsLoader;
